import React, { FC, useState } from 'react';
import { generateFontImage } from 'src/components/ArtworkApproval/utils/utils';
import { ProductMainPrice } from 'src/components/BundleProduct/ProductMainPrice';
import { ProductLocation } from 'src/components/CustomisationPage/feature/customisation.types';
import { OneTimeSetupFee } from 'src/components/BundleProduct/OneTimeSetupFee';
import { ApplicationMethod } from 'src/components/CustomisationPage/feature/customisation.types';

import { StepContainer } from '../BundleProduct/StepContainer';
import { Tag } from '../BundleProduct/Tag';

import { MultipleStepCustomisation } from './MultipleStepCustomisation/MultipleStepCustomisation';
import classes from './Customisation.scss';
import { CloseButton } from './CloseButton';
import { DoubleButton } from './DoubleButton';
import { Customisation as CustomisationType } from './types';

type CustomisationCost = {
    regularPrice: number;
    finalPrice: number;
    fee: number;
};

type Props = {
    locations: ProductLocation[];
    recommendedApplication?: ApplicationMethod;
    customisation: CustomisationType;
    onChangeCustomisation(customisation: Partial<CustomisationType>): void;
    onDeleteCustomisation(index: number): void;
    index: number;
    maxQuantity: number;
    customisationCost: CustomisationCost;
    alreadySelectedPositions?: string[];
};

export const Customisation: FC<Props> = ({
    locations,
    recommendedApplication,
    customisation,
    onChangeCustomisation,
    onDeleteCustomisation,
    index,
    maxQuantity,
    customisationCost,
    alreadySelectedPositions,
}) => {
    const [open, setOpen] = useState(false);
    const freeCustomisation = index === 0;
    const placeholder = require('../../../static/img/placeholder.jpg');
    const placeholderLogo = require('../../../static/img/placeholder-logo.jpg');

    const getCustomisationTitle = (free: string) => {
        if (customisation.selectedMethod === '3') {
            if (customisation.selectedType === 'text') return `${free} Embroidery Text`;
            else return `${free} Embroidery Logo`;
        } else {
            if (customisation.selectedType === 'text') return `${free} Print Text`;
            else return `${free} Print Logo`;
        }
    };

    return (
        <>
            <MultipleStepCustomisation
                locations={locations}
                isOpenModal={open}
                closeModal={() => setOpen(false)}
                onSubmitCustomisation={onChangeCustomisation}
                customisation={customisation}
                freeCustomisation={freeCustomisation}
                avoidSelectingPosition={index !== 0}
                recommendedApplication={recommendedApplication}
                alreadySelectedPositions={alreadySelectedPositions}
            />
            <StepContainer className={classes.wrapper}>
                <div className={classes.info}>
                    <div className={classes.customisationInfo}>
                        <div className={classes.customisationInfoTitle}>
                            <figure className={classes.customisationsImage}>
                                {(customisation.selectedType === 'logo' && customisation.selectedOptions && (
                                    <>
                                        {'isContactRequested' in customisation.selectedOptions && (
                                            <img
                                                src={placeholderLogo}
                                                alt="We will contact you"
                                                className={classes.img}
                                            />
                                        )}
                                        {'image' in customisation.selectedOptions && (
                                            <img
                                                src={customisation.selectedOptions?.image || placeholder}
                                                alt="Customisation logo"
                                                className={classes.img}
                                            />
                                        )}
                                    </>
                                )) || <img src={placeholder} alt="Placeholder logo" className={classes.img} />}
                                {customisation.selectedType === 'text' &&
                                    generateFontImage(customisation.selectedOptions)}
                            </figure>
                            <div className={classes.title}>
                                {getCustomisationTitle(freeCustomisation ? 'Free' : '')}
                            </div>
                        </div>
                        <CloseButton onClick={() => onDeleteCustomisation(index)} background="gray" />
                    </div>
                    {freeCustomisation && <Tag background="orange">FREE x {maxQuantity} pcs</Tag>}
                    <Tag>{customisation.selectedPositions}</Tag>
                </div>
                <div className={classes.pricesAndButtons}>
                    <div className={classes.prices}>
                        {customisationCost.regularPrice !== customisationCost.finalPrice && (
                            <span className={classes.regularPrice}>
                                <ProductMainPrice
                                    price={{
                                        price: customisationCost.regularPrice,
                                        price_incl: customisationCost.regularPrice,
                                    }}
                                />
                            </span>
                        )}
                        <span className={classes.currentPrice}>
                            <ProductMainPrice
                                price={{
                                    price: customisationCost.finalPrice,
                                    price_incl: customisationCost.finalPrice,
                                }}
                            />
                        </span>
                    </div>
                    <DoubleButton
                        background="white"
                        onclick={() => setOpen(true)}
                        secondaryOnClick={() => onDeleteCustomisation(index)}
                        text="Edit"
                        secondaryText="Delete"
                        icon="edit.svg"
                        secondaryIcon="delete.svg"
                        dimensions="big"
                    />
                </div>
            </StepContainer>
            {customisationCost.fee > 0 && <OneTimeSetupFee customisationFee={customisationCost.fee} />}
        </>
    );
};
